import { formatAvailability, formatRoomPrice } from '../../rooms'
import type { RoomType } from '../../types'

interface RoomChoicesProps {
  types: readonly RoomType[]
  /** The kind this invite already holds, so it can be marked. */
  currentId: string | null
  /** Which room's button is mid-request, or null. */
  busyId: string | null
  /** Omitted when the guest cannot reserve, which makes the list read-only. */
  onReserve?: (roomTypeId: string) => void
  /**
   * Backs out of changing room, keeping the reservation as it is. Only present
   * when there is an existing booking to keep.
   */
  onKeepCurrent?: () => void
}

/**
 * The rooms on offer, one card each.
 *
 * Doubles as a read-only list when `onReserve` is omitted - for somebody who has
 * not replied yet, the rooms are still worth seeing while they decide.
 */
export function RoomChoices({
  types,
  currentId,
  busyId,
  onReserve,
  onKeepCurrent,
}: RoomChoicesProps) {
  return (
    <ul className="space-y-3">
      {types.map((room) => {
        const held = room.id === currentId
        const full = room.reserved >= room.capacity
        // The room they are already in is never "full" to them: they are one of
        // the people filling it, and offering it back is how they stay put.
        const takeable = onReserve !== undefined && (!full || held)

        return (
          <li
            key={room.id}
            className={`rounded-lg border p-4 transition-colors sm:p-5 ${
              held ? 'border-sage bg-sage/5' : 'border-ink/10 bg-parchment/50'
            }`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <h4 className="font-display text-lg">{room.name}</h4>
              <p className="text-ink-soft text-sm whitespace-nowrap">
                {formatRoomPrice(room.price_pence)}
              </p>
            </div>

            <p className="text-ink-soft mt-2 text-sm">{room.description}</p>

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <p
                className={`text-xs tracking-widest uppercase ${
                  full && !held ? 'text-ink-soft' : 'text-sage'
                }`}
              >
                {held ? 'Yours' : formatAvailability(room)}
              </p>

              {takeable && !held && (
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => onReserve(room.id)}
                  className="border-ink/20 hover:border-claret hover:text-claret rounded-md border px-4 py-2 text-xs tracking-widest uppercase transition-colors disabled:opacity-50"
                >
                  {busyId === room.id ? 'Reserving…' : 'Reserve'}
                </button>
              )}

              {/* The way out of changing room sits on the room they already
                    have, beside the Reserve buttons it is an alternative to -
                    that is where somebody deciding is already looking. It only
                    closes the picker; the reservation is untouched, so this
                    costs no request and cannot fail. */}
              {held && onKeepCurrent !== undefined && (
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={onKeepCurrent}
                  className="border-sage text-sage hover:bg-sage/10 rounded-md border px-4 py-2 text-xs tracking-widest uppercase transition-colors disabled:opacity-50"
                >
                  Keep this room
                </button>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
